import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@material-ui/core";
import IntlMessages from "util/IntlMessages";
import FormInput from "components/Form/Controls/input";

const ReadOnlyField = ({ label, value }) => (
  <TextField
    variant="outlined"
    size="small"
    label={label}
    value={value || ""}
    fullWidth
    disabled
    margin="normal"
  />
);

const ReassignAlternateCodeDialog = ({ open, patient, onClose, onSave }) => {
  const validationSchema = {
    alternateCode: yup
      .string()
      .trim()
      .required(<IntlMessages id="core.fieldRequired" />),
  };

  const methods = useForm({
    defaultValues: { alternateCode: "" },
    resolver: yupResolver(yup.object().shape(validationSchema)),
  });
  const { handleSubmit, reset, errors } = methods;

  useEffect(() => {
    if (!open || !patient) return;
    reset({ alternateCode: patient.alternateCode || "" });
  }, [open, patient, reset]);

  const onSubmit = (data) => {
    if (!patient) return;

    onSave({
      personId: patient.personId ?? patient.PersonId ?? 0,
      prescriptionId: patient.prescriptionId ?? patient.PrescriptionId ?? 0,
      alternateCode: data.alternateCode.trim(),
    });
  };

  const code = patient ? patient.patientCode || patient.code : "";
  const fullName = patient ? patient.patient || patient.fullName : "";
  const productline = patient ? patient.productline || patient.productLine : "";

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      disableBackdropClick
      onClose={onClose}
    >
      <DialogTitle>
        <IntlMessages id="patient.reassignAlternateCode" />
      </DialogTitle>
      <DialogContent className="new-patient-dialog-content">
        {patient && (
          <FormProvider {...methods}>
            <form className="row" noValidate autoComplete="off">
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <ReadOnlyField
                    label={<IntlMessages id="core.code" />}
                    value={code}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReadOnlyField
                    label={<IntlMessages id="core.documentOnly" />}
                    value={patient.document}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ReadOnlyField
                    label={<IntlMessages id="core.patient" />}
                    value={fullName}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReadOnlyField
                    label={<IntlMessages id="core.productLine" />}
                    value={productline}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReadOnlyField
                    label={<IntlMessages id="core.city" />}
                    value={patient.city}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInput
                    name="alternateCode"
                    label={<IntlMessages id="core.alternateCodeAbbr" />}
                    margin="normal"
                    required={true}
                    errorobj={errors}
                  />
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        )}
      </DialogContent>
      <DialogActions className="modal-actions">
        <Button onClick={handleSubmit(onSubmit)} variant="contained" color="primary">
          <IntlMessages id="core.save" />
        </Button>
        <Button onClick={onClose} color="secondary">
          <IntlMessages id="core.cancel" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReassignAlternateCodeDialog;
